"use client";

import { ConfigProvider, Select } from "antd";
import { DownOutlined } from "@ant-design/icons";
import down from "@assets/images/down.svg";
import style from "../style.module.scss"; // Style as needed
import localStyle from "./CNConverter.style.module.scss";
import React, { useEffect, useState } from "react";
import SaveQuit from "@commonComponents/save-quit";
import { constRoute } from "@utils/route";
import { useNavigate } from "react-router-dom";
import { notification } from "@utils/notifications";
import ProjectDeleteModelData from "@components/pages/ExistingProject/projectDeleteModel";
import { useStore } from "@stores/root-store";
import QuickDelete from "@assets/images/Quick-Delete.svg";

export const PROPOSAL_SECTIONS = [
  {
    label: "B. Proposal Executive Summary",
    value: "B",
    subsections: [],
  },
  {
    label: "C. Proposal Information",
    value: "C",
    subsections: [
      { label: "C.1 Climate Change Context", value: "C.1" },
      { label: "C.2 Proposed Project/Programme", value: "C.2" },
      {
        label: "C.3 Country Ownership and Engagement with Stakeholders",
        value: "C.3",
      },
      { label: "C.4 Indicative Safeguards Profile", value: "C.4" },
      {
        label: "C.5 Exit Strategy and Sustainability of Impacts",
        value: "C.5",
      },
    ],
  },
  {
    label: "D. Indicative Financing Information",
    value: "D",
    subsections: [
      { label: "D.1 Total Financing", value: "D.1" },
      {
        label: "D.2 Indicative Terms Non-Grants Only",
        value: "D.2",
        disabled: true,
      },
      { label: "D.3 Financial Structure", value: "D.3" },
      { label: "D.4 Justification of GCF Funding Request", value: "D.4" },
    ],
  },
  {
    label: "E. Accredited Entity/Executing Entity Information",
    value: "E",
    subsections: [],
  },
  {
    label: "F. Key Risks/Mitigation Measures",
    value: "F",
    subsections: [],
  },
];

type SectionArray = typeof PROPOSAL_SECTIONS & {
  getLabel: (value: string) => string | undefined;
};

export const CN_CONVERTER_PLUGIN_SECTIONS = PROPOSAL_SECTIONS as SectionArray;

CN_CONVERTER_PLUGIN_SECTIONS.getLabel = (value: string) => {
  for (const section of CN_CONVERTER_PLUGIN_SECTIONS) {
    if (section.value === value) return section.label;
    const sub = section.subsections.find((s) => s.value === value);
    if (sub) return sub.label;
  }
  return undefined;
};

type Props = {
  section: string;
  subsection: string;
  setSection: (val: string) => void;
  setSubsection: (val: string) => void;
  setBlockSubmission?: (val: boolean) => void;
};

export default function CNConverter({
  section,
  subsection,
  setSection,
  setSubsection,
  setBlockSubmission,
}: Props) {
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();

  const sectionOptions = PROPOSAL_SECTIONS.map((section) => ({
    label: section.label,
    value: section.value,
  }));

  const {
    user: { getLoadingDeleteRecord, getProjectNameData, projectDelete },
  } = useStore(null);

  const projectName = getProjectNameData
    ? JSON.parse(getProjectNameData).project_name
    : localStorage.getItem("projectName");

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const deleteProjectData = async () => {
    const payload = {
      project_name: projectName,
      functionality: "concept note converter",
    };

    const res = await projectDelete(payload);
    if (
      res?.error?.includes("Invalid token") ||
      res?.error?.includes("Token has expired")
    ) {
    } else {
      setIsDeleteModalOpen(false);
      notification.success("Project data deleted successfully!");
      navigate(constRoute.home);
    }
  };

  const selectedSection = PROPOSAL_SECTIONS.find((s) => s.value === section);
  const subsectionOptions =
    selectedSection?.subsections.map((sub) => ({
      label: sub.label,
      value: sub.value,
      disabled: sub.disabled || false,
    })) || [];

  useEffect(() => {
    // Check if a section is selected, if not block submission
    // if (selectedSection === undefined) {
    //   setBlockSubmission(true);
    //
    //   return;
    // }

    // Check if the selected section has subsections
    if (selectedSection?.subsections.length > 0 && !subsection) {
      setBlockSubmission(true);
    } else {
      setBlockSubmission(false);
    }
  }, [section, subsection]);

  const handleDummySaving = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      notification.success("Project saved successfully!");
      navigate(constRoute.existingProject);
    }, 2000);
  };

  return (
    <>
      <ProjectDeleteModelData
        loading={getLoadingDeleteRecord}
        isOpen={isDeleteModalOpen}
        closeModal={() => setIsDeleteModalOpen(false)}
        onDeleteRecord={deleteProjectData}
      />
      <ConfigProvider
        theme={{
          components: {
            Select: {
              optionHeight: 32,
              colorBorder: "#FFDDA6",
              hoverBorderColor: "#FFDDA6",
              activeBorderColor: "#FFDDA6",
              activeOutlineColor: "#FFDDA6",
            },
          },
        }}
      >
        <Select
          style={{ marginLeft: "25px", width: "330px", textAlign: "start" }}
          suffixIcon={<img src={down} alt={"down icon"} />}
          onChange={(value) => {
            if (value === "close") return;
            setSection(value);
            setSubsection("");
          }}
          value={section || undefined}
          placement="topRight"
          placeholder="Select a Section"
          options={[
            ...sectionOptions,
            {
              label: (
                <span
                  style={{
                    display: "flex",
                    height: "100%",
                    alignItems: "flex-end",
                    justifyContent: "center",
                  }}
                >
                  <DownOutlined />
                </span>
              ),
              value: "close",
            },
          ]}
          dropdownRender={(menu) => (
            <div className={style.dropdownMultiline}>{menu}</div>
          )}
        />

        <Select
          defaultValue="Select a Subsection"
          placeholder="Select a Subsection"
          placement="topRight"
          disabled={
            !selectedSection || selectedSection.subsections.length === 0
          }
          style={{ width: "350px", textAlign: "start", marginLeft: "25px" }}
          suffixIcon={<img src={down} alt={"down icon"} />}
          options={[
            ...subsectionOptions,
            {
              label: (
                <span
                  style={{
                    display: "flex",
                    height: "100%",
                    alignItems: "flex-end",
                    justifyContent: "center",
                  }}
                >
                  <DownOutlined />
                </span>
              ),
              value: "close",
            },
          ]}
          value={subsection || undefined}
          onChange={(value) => {
            if (value === "close") return;
            setSubsection(value);
          }}
          dropdownRender={(menu) => (
            <div className={style.dropdownMultiline}>{menu}</div>
          )}
        />
        <span
          className={style.clearDropdowns}
          onClick={() => {
            setSection("");
            setSubsection("");
          }}
        >
          Clear
        </span>
      </ConfigProvider>

      <span style={{ margin: "5px 5px 0px auto", scale: 0.9 }}>
        <img
          onClick={() => setIsDeleteModalOpen(true)}
          src={QuickDelete}
          alt="Quick Delete"
          className={localStyle.bottomBarButton}
        />
      </span>

      <span style={{ margin: "5px 20px 0px 5px", scale: 0.9 }}>
        <SaveQuit
          onClickForFooter={handleDummySaving}
          isSaving={isSaving}
          className={localStyle.bottomBarButton}
          buttonStyle={"alt"}
        />
      </span>
      {/*<span*/}
      {/*  className={style.clearDropdowns}*/}
      {/*  onClick={() => {*/}
      {/*    setSection("");*/}
      {/*    setSubsection("");*/}
      {/*  }}*/}
      {/*>*/}
      {/*  Clear*/}
      {/*</span>*/}
    </>
  );
}

export function CNConverterTooltip1() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const tooltipLocalStorage = localStorage.getItem(
      "showConceptNoteConverterTooltip1",
    );
    if (tooltipLocalStorage === "false") {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [localStorage.getItem("showConceptNoteConverterTooltip1")]);

  if (open)
    return (
      <div className={localStyle.conceptNoteStep1}>
        <div className={localStyle.conceptNoteStep1Tooltip}>
          <div className={localStyle.conceptNoteStep1TooltipTop}>
            Step 1. Select relevant section
          </div>
          <div className={localStyle.conceptNoteStep1TooltipContent}>
            Choose a section for conversion below (these sections refer to the
            new GCF template v3.0).
          </div>
          <div className={localStyle.conceptNoteStep1TooltipFooter}>
            <button
              onClick={(e) => {
                e.preventDefault();
                localStorage.setItem(
                  "showConceptNoteConverterTooltip1",
                  "false",
                );
                setOpen(false);
              }}
            >
              Got It
            </button>
          </div>
        </div>
      </div>
    );
}

export function CNConverterTooltip2() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const tooltipLocalStorage = localStorage.getItem(
      "showConceptNoteConverterTooltip2",
    );
    if (tooltipLocalStorage === "false") {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [localStorage.getItem("showConceptNoteConverterTooltip2")]);

  if (open)
    return (
      <>
        <div className={localStyle.conceptNoteStep2Tooltip}>
          <div className={localStyle.conceptNoteStep2TooltipTop}>
            Step 2. Select relevant subsection
          </div>
          <div className={localStyle.conceptNoteStep2TooltipContent}>
            Select the subsection if applicable (these subsections also refer to
            the new GCF template v3.0).
          </div>
          <div className={localStyle.conceptNoteStep2TooltipFooter}>
            <button
              onClick={(e) => {
                e.preventDefault();
                localStorage.setItem(
                  "showConceptNoteConverterTooltip2",
                  "false",
                );
                setOpen(false);
              }}
            >
              Got It
            </button>
          </div>
        </div>
      </>
    );
}

export function CNConverterTooltip3() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const tooltipLocalStorage = localStorage.getItem(
      "showConceptNoteConverterTooltip3",
    );
    if (tooltipLocalStorage === "false") {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [localStorage.getItem("showConceptNoteConverterTooltip3")]);

  if (open)
    return (
      <>
        <div className={localStyle.conceptNoteStep3}>
          <div className={localStyle.conceptNoteStep3Tooltip}>
            <div className={localStyle.conceptNoteStep3TooltipTop}>
              Step 3. Upload your concept note
            </div>
            <div className={localStyle.conceptNoteStep3TooltipContent}>
              Click on the “+” button, select the “Upload a Concept Note”
              feature, then press the ‘Submit’ button.
            </div>
            <div className={localStyle.conceptNoteStep3TooltipFooter}>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  localStorage.setItem(
                    "showConceptNoteConverterTooltip3",
                    "false",
                  );
                  setOpen(false);
                }}
              >
                Got It
              </button>
            </div>
          </div>
        </div>
      </>
    );
}
